import React, { useCallback, useEffect, useMemo } from 'react';
import { isBoolean } from 'lodash';

import beFeed from 'actions/activity/beFeed.action';
import useAfn from 'hooks/useAfn.hook';
import useIsPersonalCalendarOnly from 'hooks/useIsPersonalCalendarOnly.hook';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import { useDispatcher } from 'providers/Dispatcher.provider';
import { useTranslation } from 'providers/GILT.provider';
import { PUSHER_EVENT_BOOKING_TYPE, PUSHER_EVENT_INFO, PUSHER_EVENT_MISSED_CALL, PUSHER_EVENT_ONLINE_VOUCHER_SALE, PUSHER_EVENT_SMS_THREAD } from 'providers/Pusher.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useStored } from 'providers/SelectorProvider.provider';
import { selectActivityFeedData } from 'selectors/activity.selectors';
import { selectClientCommsMessageSent } from 'selectors/clientCommunications.selectors';
import { selectCurrentSalonClients } from 'selectors/currentSalon.selectors';
import getAnyOrVoidUtil from 'utils/accessors/getAnyOrVoid.util';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromObjectToEntries from 'utils/converters/object/fromObjectToEntries.util';
import { fromAnyDateToZuluDayBgn } from 'utils/converters/time/from.util';
import { formatDayToYearMidWeekday, formatDayToYearStandard, formatDayToYearWeekdayInt, formatPartTime, formatYearToDayCompact } from 'utils/date/format.util';
import { nowAsDate, nowAsMoment } from 'utils/date/now.util';
import timeDiffDays from 'utils/date/timeDiffDays.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import createNodeKey from 'utils/react/createNodeKey.util';

import { markActivityFeedItemAsNotSeen, markAsSeen, mutuCallNumber } from 'activity/actions';
import BookingItem from 'activity/components/BookingItem';
import Footer from 'activity/components/ItemFooter';
import MissedCallContainer from 'activity/components/MissedCall';
import OnlineVoucherSale from 'activity/components/OnlineVoucherSale';
import SmsThreadItem from 'activity/components/SmsThreadItem';
import TodaysOverviewContainer from 'activity/components/TodaysOverview';
import { getAppointment } from 'checkout/actions';

import 'styles/activityfeed/feed.scss';
import CN from './Activity.module.scss';

const L = logger('Activity');

const determineDisplay = (date) => {
  const day = fromAnyDateToZuluDayBgn(date);
  const diff = timeDiffDays({ bgn: fromAnyDateToZuluDayBgn(nowAsMoment()), end: day });

  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';

  return formatDayToYearWeekdayInt(day);
};

const UnhandledItem = ({ description, onReadToggle, seen, timestamp, title }) => (
  <div className="comment-content 2">
    <div className={!seen ? 'new-item-indicator available' : 'new-item-indicator'} />
    <div className="feed-text 2">
      <span className="welcome-title cfix">{title}</span>
      <div className="additional-info">{description}</div>
    </div>
    <Footer seen={seen} markAsRead={() => onReadToggle(!seen)} timestamp={timestamp} />
  </div>
);

const ActivityFeedItem = ({ history, value: valueFromProps }) => {
  const {
    type,
    client_name: clientName,
    sent_text: sentText,
    description,
    received_text: receivedText,
    target_id: targetId,
    stylist_name: stylistId,
    stylist_id: feedStylistId,
    outcome,
    title,
    client_phone: clientPhone,
    newStartTime,
    seen,
    notes,
    clientId: clientId1,
    client_id: clientId2,
    service_name: serviceName,
    booking_start_time: bookingStartTime,
    timestamp: timestampFromItem,
    id: itemId,
    call_id: callId,
    amount,
  } = valueFromProps ?? {};

  const { isPersonalOnly, accessStylistId } = useIsPersonalCalendarOnly();

  const dispatchMarkAsSeen = useDispatcher(markAsSeen);
  const dispatchMarkActivityFeedItemAsNotSeen = useDispatcher(markActivityFeedItemAsNotSeen);
  const dispatchMutuCallNumber = useDispatcher(mutuCallNumber);
  const dispatchGetAppointment = useDispatcher(getAppointment);

  const salonId = useCurrentSalonId();
  const clients = useStored(selectCurrentSalonClients);
  const feedData = useStored(selectActivityFeedData);
  const messageSent = useStored(selectClientCommsMessageSent);
  const { t } = useTranslation();

  const [localSeen, setLocalSeen] = useStateWhileMounted(seen);
  const handleCallNumber = useCallback((phone) => dispatchMutuCallNumber(salonId, phone), [dispatchMutuCallNumber, salonId]);

  const [{ value: valueFromToggle, isLoading, error }, handleReadToggle] = useAfn(
    async (value) => {
      L.info('Marking itemId:', itemId, 'as', value ? 'seen' : 'not seen', '...');

      const result = value ? await dispatchMarkAsSeen(itemId, callId, salonId) : await dispatchMarkActivityFeedItemAsNotSeen(itemId);

      const isOk = getAnyOrVoidUtil(value ? 'feedResult.is.ok' : 'is.ok', result);
      if (!isOk) {
        L.warn('handleReadToggle()', 'Failed setting itemId:', itemId, 'result:', result);
        return;
      }

      // NOTE: only the version for true returns a result from BE, we assume the other one based on the 200 status code
      const seenFromMarkAsSeen = getAnyOrVoidUtil('feedResult.json.item.seen', result);
      setLocalSeen(value && isBoolean(seenFromMarkAsSeen) ? seenFromMarkAsSeen : value);
    },
    [itemId, dispatchMarkAsSeen, callId, salonId, dispatchMarkActivityFeedItemAsNotSeen, setLocalSeen],
  );

  const handleGoToSchedulerAndOpenModal = useCallback(
    (appointmentId, timestamp) => {
      const definedTimestamp = nowAsDate().getTime();
      const pathname = `/salon/${salonId}/day/${formatYearToDayCompact(timestamp)}/`;
      const state = { showModalAction: { appointmentId, dateToSwitchTo: timestamp, definedTimestamp } };

      history.push({ pathname, state });
    },
    [history, salonId],
  );

  const timestamp = formatPartTime(timestampFromItem);

  if (error) L.warn('ActivityFeedItem()', isLoading, error, valueFromToggle);
  if (!valueFromProps) return null;

  if (PUSHER_EVENT_BOOKING_TYPE === type) {
    return (
      <BookingItem
        time={formatPartTime(bookingStartTime)}
        date={formatDayToYearMidWeekday(bookingStartTime)}
        salonId={salonId}
        booking_start_time={bookingStartTime}
        bookingID={targetId}
        clientID={clientId2 || clientId1}
        clientName={clientName}
        serviceName={serviceName}
        stylistName={stylistId}
        timestamp={timestamp}
        notes={notes}
        seen={localSeen}
        newStartTime={newStartTime}
        history={history}
        onReadToggle={handleReadToggle}
        goToSchedulerAndOpenModal={handleGoToSchedulerAndOpenModal}
        messageSent={messageSent}
        getAppointment={dispatchGetAppointment}
        feedData={feedData}
        hideViewAppointment={isPersonalOnly && feedStylistId !== accessStylistId}
      />
    );
  }

  if (PUSHER_EVENT_INFO === type) {
    return <TodaysOverviewContainer history={history} timestamp={timestamp} seen={localSeen} onReadToggle={handleReadToggle} goToSchedulerAndOpenModal={handleGoToSchedulerAndOpenModal} />;
  }

  if (PUSHER_EVENT_SMS_THREAD === type) {
    return (
      <SmsThreadItem
        clientName={clientName}
        clientID={clientId1 || clientId2}
        history={history}
        receivedText={receivedText}
        sentText={sentText}
        timestamp={timestamp}
        seen={localSeen}
        onReadToggle={handleReadToggle}
        booking_start_time={bookingStartTime}
        bookingID={targetId}
        messageSent={messageSent}
        goToSchedulerAndOpenModal={handleGoToSchedulerAndOpenModal}
        hideViewAppointment={isPersonalOnly}
      />
    );
  }

  if (PUSHER_EVENT_MISSED_CALL === type) {
    return (
      <MissedCallContainer
        title={t('composite.activityFeed.missedCall.title')}
        callNumber={handleCallNumber}
        history={history}
        number={clientPhone}
        outcome={outcome}
        clients={asArray(clients)}
        timestamp={timestamp}
        seen={localSeen}
        onReadToggle={handleReadToggle}
      />
    );
  }

  if (PUSHER_EVENT_ONLINE_VOUCHER_SALE === type) {
    return <OnlineVoucherSale amount={amount} clientName={clientName} onReadToggle={handleReadToggle} seen={localSeen} salonId={salonId} timestamp={timestamp} />;
  }

  if (PUSHER_EVENT_INFO === type) {
    return <UnhandledItem title={title} history={history} description={description} timestamp={timestamp} seen={localSeen} onReadToggle={handleReadToggle} />;
  }

  return (
    <UnhandledItem
      title={t('composite.activityFeed.unhandledTitle')}
      history={history}
      description={t('composite.activityFeed.unhandledDescription', { type })}
      timestamp={timestamp}
      seen={localSeen}
      onReadToggle={alwaysNoop}
    />
  );
};

const ActivityFeedItems = ({ date, entries, ...extra }) => (
  <div data-bem="ActivityFeedItems">
    <div data-bem="ActivityFeedItem__stamp" className="comment-timestamp days">
      {determineDisplay(date)}
    </div>

    {asArray(entries).map((value) => (
      <ActivityFeedItem key={createNodeKey({ tag: `${value.type}: ${value.id}`, unique: value.id })} value={value} {...extra} />
    ))}
  </div>
);

const Activity = ({ history, params }) => {
  const salonId = useCurrentSalonId();
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useStateWhileMounted(1);

  const dispatchBeFeed = useDispatcher(beFeed);

  const [{ isLoading, isLoaded, value, error }, fetchFeed] = useAfn(
    async (page) => {
      const result = await dispatchBeFeed({ page });
      L.debug('fetchFeed()', 'page:', page, 'result:', result);

      const json = asObject(result?.json);
      setCurrentPage(page);

      return json;
    },
    [dispatchBeFeed, setCurrentPage],
  );

  const hasPrev = fromAnyToBool(value?.previous);
  const hasNext = fromAnyToBool(value?.next);
  const items = asArray(value?.results);
  const displayTextNext = hasPrev ? t('composite.activityFeed.nextButton') : t('composite.activityFeed.showMoreButton');

  const days = useMemo(
    () =>
      items.reduce((object, item) => {
        const day = formatDayToYearStandard(item?.timestamp);
        return { ...object, [day]: [...asArray(object[day]), item] };
      }, {}),
    [items],
  );

  const handleClose = useCallback(() => void history.goBack(), [history]);
  const handlePrev = useCallback(() => void fetchFeed(currentPage - 1), [fetchFeed, currentPage]);
  const handleNext = useCallback(() => void fetchFeed(currentPage + 1), [fetchFeed, currentPage]);

  useEffect(
    // kick off by fetching the default page, after necessary checks are done, of course
    () => void (salonId && !items.length && !isLoading && !isLoaded && fetchFeed(1)),
    [salonId, fetchFeed, isLoading, isLoaded, items.length],
  );

  useMemo(() => L.debug('()', ...L.O({ days, items, value })), [days, items, value]);
  if (error) L.warn('()', { isLoading, isLoaded, error, value });

  return (
    <div data-bem="Activity" className="feed-sidebar animated slideInRight">
      <div data-bem="Activity__content" className="container">
        <div className="feed-header">
          {t('composite.activityFeed.title')}
          <div role="button" tabIndex={0} className="close-button ico-close" onClick={handleClose} />
        </div>
        <div className="sidebar-menu">
          <div data-bem="Activity__items" className="comment-content-block">
            {fromObjectToEntries(days).map(([key, list]) => (
              <ActivityFeedItems key={key} date={list?.[0]?.timestamp} params={params} history={history} entries={list} />
            ))}

            <div data-bem="Activity__more" className="sidebar-more-wrap">
              <div data-bem="Activity__buttons" className={CN.buttons}>
                {hasPrev && (
                  <button data-bem="Activity__prev" className="btn btn-violet" onClick={handlePrev}>
                    {t('composite.activityFeed.previousButton')}
                  </button>
                )}
                {hasNext && (
                  <button data-bem="Activity__next" className="btn btn-violet" id="moreBtn" onClick={handleNext}>
                    {displayTextNext}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div data-bem="Activity__close-button" className="close-feed-overlay" onClick={handleClose} role="button" tabIndex="0" />
    </div>
  );
};

// this extra export is needed for tests that provide their own mock for connect()
export { ActivityFeedItem as ActivityFeedItem4Test };
export { ActivityFeedItems as ActivityFeedItems4Test };
export { UnhandledItem as UnhandledItem4Test };
export { Activity as Activity4Test };

export default Activity;
